<template>
    <RegularModal ref="modal" modal-card-class="is-medium">
        <!-- Modal title -->
        <template v-slot:title> {{ translate("divide_category_modal.title") }}: {{ targetCategoryName }} </template>

        <!-- Modal body -->
        <template v-slot:body>
            <div>
                <Notification class="is-danger" v-if="dividedCategoryPreview.blockedByBracket">
                    {{ translate("divide_category_modal.blocked_by_bracket") }}
                </Notification>

                <div class="mb-2">
                    {{ translate("divide_category_modal.total_competitors") }}:
                    <strong>{{ dividedCategoryPreview.totalCompetitors }}</strong>
                </div>

                <div class="is-flex" v-if="dividedCategoryPreview.totalCompetitors > 1">
                    <div class="field mr-2">
                        <label class="label"> {{ translate("divide_category_modal.max_size") }}: </label>
                        <div class="control">
                            <input
                                class="input"
                                type="number"
                                min="1"
                                v-model.number="maxGroupSize"
                                :placeholder="translate('divide_category_modal.max_size_placeholder')"
                            />
                        </div>
                    </div>

                    <div class="field">
                        <label class="label">{{ translate("divide_category_modal.dividing_strategy") }}:</label>
                        <div class="control">
                            <div class="select">
                                <select v-model="selectedDividingStrategy">
                                    <option :value="DividingStrategyEnum.RegistrationDate">
                                        {{ translate("divide_category_modal.strategy_registration_date") }}
                                    </option>
                                    <option :value="DividingStrategyEnum.Academy">
                                        {{ translate("divide_category_modal.strategy_academy") }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-2" v-if="dividedCategoryPreview.totalCompetitors > 1">
                    <h5 class="mb-2">{{ translate("divide_category_modal.preview") }}:</h5>
                    <div class="columns is-centered is-multiline">
                        <div
                            v-for="(group, index) in dividedCategoryPreview.registeredCompetitors"
                            :key="index"
                            class="column"
                            :class="{
                                'is-one-third': dividedCategoryPreview.registeredCompetitors.length > 3,
                                'is-half':
                                    dividedCategoryPreview.registeredCompetitors.length <= 3 &&
                                    dividedCategoryPreview.registeredCompetitors.length > 1,
                                '': dividedCategoryPreview.registeredCompetitors.length === 1,
                            }"
                        >
                            <div class="box">
                                <p class="mb-1 has-text-weight-bold">
                                    {{ translate("divide_category_modal.group") }} {{ index + 1 }} =
                                    {{ translate("divide_category_modal.competitors", false, [group.length]) }}
                                </p>
                                <ul>
                                    <li v-for="(competitor, idx) in group" :key="idx">
                                        {{ idx + 1 }}.
                                        <span class="clarity-mask">{{ competitor.firstName }} {{ competitor.lastName }}</span>
                                        ({{ competitor.academy }})
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="dividedCategoryPreview.totalCompetitors <= 1">
                    <div class="columns is-centered">
                        <div class="column is-half">
                            <Notification class="notification is-warning">
                                {{ translate("divide_category_modal.too_small_to_divide") }}
                            </Notification>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    <span class="icon">
                        <i class="fa fa-times" />
                    </span>
                    <span>{{ translate("common.cancel") }}</span>
                </button>
                <LoadingButton
                    :is-loading="confirmInProgress"
                    class="button is-primary"
                    :disabled="!confirmPossible"
                    @click="confirmDivision"
                >
                    <span class="icon">
                        <i class="fa fa-check" />
                    </span>
                    <span>{{ translate("common.ok") }}</span>
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import { useModal } from "../Common/useModal";
    import dividingCategoriesApi, {
        DividedCategoryPreview,
        DividingStrategy,
        DividingStrategyEnum,
    } from "../Api/Categories/dividingCategoriesApi";
    import { handleError } from "../Common/errorHandler";
    import debounce from "lodash.debounce";
    import Notification from "../Common/Notification.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { translate } from "../Common/translations";

    const props = defineProps<{
        eventId: number;
    }>();

    const { modal, open: openModal, close } = useModal();

    const dividedCategoryPreview = ref<DividedCategoryPreview>({
        registeredCompetitors: [],
        totalCompetitors: 0,
        blockedByBracket: false,
    });
    const maxGroupSize = ref<number>(0);
    const targetParameterId = ref<number>(0);
    const targetCategoryName = ref<string>("");
    const initDone = ref<boolean>(false);

    watch(
        maxGroupSize,
        debounce(() => {
            return refreshDividedCategoryPreview();
        }, 500),
    );

    const selectedDividingStrategy = ref<DividingStrategy>(DividingStrategyEnum.RegistrationDate);
    watch(selectedDividingStrategy, () => {
        return refreshDividedCategoryPreview();
    });

    function refreshDividedCategoryPreview() {
        return dividingCategoriesApi
            .previewDividedCategory({
                eventId: props.eventId,
                parameterId: targetParameterId.value,
                desiredSizeOfSubCategories: Number(maxGroupSize.value) || 0,
                dividingStrategy: selectedDividingStrategy.value,
            })
            .then(response => {
                dividedCategoryPreview.value = response;
            })
            .catch(handleError);
    }

    function open(parameterId: number, categoryName: string) {
        initDone.value = false;
        targetParameterId.value = parameterId;
        targetCategoryName.value = categoryName;
        maxGroupSize.value = 0;
        openModal();

        return refreshDividedCategoryPreview().finally(() => {
            initDone.value = true;
        });
    }

    const confirmPossible = computed(() => {
        return (
            initDone.value &&
            dividedCategoryPreview.value.totalCompetitors > 1 &&
            Number.isInteger(maxGroupSize.value) &&
            maxGroupSize.value >= 1 &&
            !dividedCategoryPreview.value.blockedByBracket
        );
    });

    const confirmInProgress = ref<boolean>(false);

    const emit = defineEmits(["confirmed"]);

    function confirmDivision() {
        confirmInProgress.value = true;
        return dividingCategoriesApi
            .confirmDividedCategory({
                eventId: props.eventId,
                parameterId: targetParameterId.value,
                desiredSizeOfSubCategories: Number(maxGroupSize.value) || 0,
                dividingStrategy: selectedDividingStrategy.value,
            })
            .then(() => {
                emit("confirmed");
                close();
            })
            .catch(handleError)
            .finally(() => {
                confirmInProgress.value = false;
            });
    }

    defineExpose({ open });
</script>

<style scoped>
    .box {
        padding: 0.75rem 1rem;
    }
</style>
